import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, MessageCircle, Phone, Mail, Star, Bot, Send } from 'lucide-react';

const PremiumFloatingButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [particles, setParticles] = useState([]);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    { 
      id: 1, 
      text: "Hello! I'm your AI assistant. How can I help you today? Feel free to share your name, email, or phone number if you'd like me to save your contact information.", 
      isBot: true, 
      timestamp: new Date() 
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [contactCollected, setContactCollected] = useState(false);

  const actions = [
    {
      icon: MessageCircle,
      label: 'WhatsApp',
      color: 'from-green-500 to-emerald-500',
      action: () => window.open('https://wa.me/1234567890', '_blank')
    },
    {
      icon: Phone,
      label: 'Call Us',
      color: 'from-blue-500 to-cyan-500',
      action: () => window.open('tel:+1234567890', '_blank')
    },
    {
      icon: Mail,
      label: 'Email',
      color: 'from-purple-500 to-pink-500',
      action: () => window.open('mailto:info@sentientdigital.com', '_blank')
    },
    {
      icon: Bot,
      label: 'Chatbot',
      color: 'from-purple-500 to-indigo-500',
      action: () => setIsChatOpen(true)
    }
  ];

  const formatMessage = (text) => {
    // Simple markdown parser for basic formatting
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold text
      .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic text
      .replace(/\n/g, '<br />'); // Line breaks
  };

  const extractContactInfo = (message) => {
    const contactInfo = {
      name: null,
      email: null,
      phone_number: null
    };

    // Email regex pattern
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    const emails = message.match(emailRegex);
    if (emails && emails.length > 0) {
      contactInfo.email = emails[0];
    }

    // Phone number regex patterns (various formats)
    const phoneRegex = /(\+?[\d\s\-\(\)]{10,})/g;
    const phones = message.match(phoneRegex);
    if (phones && phones.length > 0) {
      // Clean up phone number
      contactInfo.phone_number = phones[0].replace(/[\s\-\(\)]/g, '');
    }

    // Name extraction - look for patterns like "my name is", "I'm", "call me", etc.
    const namePatterns = [
      /(?:my name is|i'm|i am|call me|this is)\s+([a-zA-Z\s]+)/i,
      /(?:name|naam)\s*:\s*([a-zA-Z\s]+)/i,
      /^([a-zA-Z\s]+)$/ // If message is just a name
    ];

    for (const pattern of namePatterns) {
      const match = message.match(pattern);
      if (match && match[1]) {
        const name = match[1].trim();
        if (name.length > 1 && name.length < 50) { // Reasonable name length
          contactInfo.name = name;
          break;
        }
      }
    }

    // If we found any contact info, return it
    if (contactInfo.name || contactInfo.email || contactInfo.phone_number) {
      return contactInfo;
    }

    return null;
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      isBot: false,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Extract contact information from user message
    const contactInfo = extractContactInfo(inputMessage);
    
    // Prepare webhook
    const CHAT_WEBHOOK = process.env.REACT_APP_CHAT_WEBHOOK || 'https://n8n.srv980418.hstgr.cloud/webhook/website-chatbot';

    // If we found contact info and haven't collected it yet, send it to webhook (best-effort)
    if (contactInfo && !contactCollected && CHAT_WEBHOOK) {
      try {
        await fetch(CHAT_WEBHOOK, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'add_contact',
            contactInfo: contactInfo,
            timestamp: new Date().toISOString()
          })
        });
        setContactCollected(true);
      } catch (error) {
        // Silently fail in demo/local mode
        console.warn('Contact webhook unavailable; running in demo mode.');
      }
    }
    
    try {
      if (!CHAT_WEBHOOK || CHAT_WEBHOOK.startsWith('http') === false) {
        throw new Error('CHAT_WEBHOOK not configured');
      }
      const response = await fetch(CHAT_WEBHOOK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage,
          timestamp: new Date().toISOString(),
          contactInfo: contactInfo // Send extracted contact info to webhook
        })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (response.ok) {
        // Check if response has content
        const responseText = await response.text();
        console.log('Raw response text:', responseText);
        
        let data = null;
        let botResponse = "Thank you for your message! I'll get back to you soon.";
        
        // Only try to parse JSON if there's actual content
        if (responseText && responseText.trim()) {
          try {
            data = JSON.parse(responseText);
            console.log('Webhook response:', data);
            
            // Try different possible response formats
            if (data.output) {
              botResponse = data.output;
            } else if (data.response) {
              botResponse = data.response;
            } else if (data.message) {
              botResponse = data.message;
            } else if (data.text) {
              botResponse = data.text;
            } else if (typeof data === 'string') {
              botResponse = data;
            } else if (data && Object.keys(data).length > 0) {
              // If there's any data, show it for debugging
              botResponse = JSON.stringify(data);
            }
          } catch (jsonError) {
            console.error('JSON parsing error:', jsonError);
            // If it's not valid JSON, treat it as plain text
            botResponse = responseText;
          }
        }

        const botMessage = {
          id: Date.now() + 1,
          text: botResponse,
          isBot: true,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
      } else {
        const errorText = await response.text();
        console.error('Webhook error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        text: "Thanks for your message! Our team will reach out shortly. (Live chat is offline in this environment)",
        isBot: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleMainButtonClick = () => {
    setIsOpen(!isOpen);
    
    // Create particle effect
    if (!isOpen) {
      const newParticles = Array.from({ length: 12 }, (_, i) => ({
        id: Date.now() + i,
        x: 50,
        y: 50,
        angle: (i * 30) * (Math.PI / 180),
        speed: 3 + Math.random() * 2
      }));
      setParticles(newParticles);
    } else {
      setParticles([]);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Chat Interface */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-24 right-0 w-80 h-96 bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200 flex flex-col"
          >
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Bot className="w-5 h-5" />
                <span className="font-semibold">AI Assistant</span>
              </div>
              <button
                onClick={() => setIsChatOpen(false)}
                className="p-1 rounded-full hover:bg-white/20 transition-colors"
                aria-label="Close chatbot"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-xs px-3 py-2 rounded-lg ${
                      message.isBot
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white'
                    }`}
                  >
                    <div 
                      className="text-sm"
                      dangerouslySetInnerHTML={{ 
                        __html: message.isBot ? formatMessage(message.text) : message.text 
                      }}
                    />
                    <p className="text-xs opacity-60 mt-1">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </motion.div>
              ))}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-gray-100 text-gray-800 px-3 py-2 rounded-lg">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="flex-1 px-3 py-2 bg-gray-800 text-white placeholder-gray-400 border border-purple-400/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={isLoading}
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                  className="px-3 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Particle effects */}
      <AnimatePresence>
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            initial={{ 
              x: particle.x, 
              y: particle.y, 
              opacity: 1, 
              scale: 1 
            }}
            animate={{
              x: particle.x + Math.cos(particle.angle) * 80,
              y: particle.y + Math.sin(particle.angle) * 80,
              opacity: 0,
              scale: 0
            }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{
              duration: 1,
              ease: "easeOut"
            }}
            className="absolute w-2 h-2 bg-blue-400 rounded-full pointer-events-none"
          />
        ))}
      </AnimatePresence>

      {/* Action buttons */}
      <AnimatePresence>
        {isOpen && (
          <div className="absolute bottom-16 right-0 space-y-4">
            {actions.map((action, index) => (
              <motion.button
                key={action.label}
                initial={{ opacity: 0, scale: 0, x: 20 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0, x: 20 }}
                transition={{
                  duration: 0.3,
                  delay: index * 0.1,
                  ease: "easeOut"
                }}
                onClick={action.action}
                className={`
                  group relative w-14 h-14 bg-gradient-to-r ${action.color} 
                  rounded-full text-white shadow-lg hover:shadow-xl 
                  transition-all duration-300 flex items-center justify-center
                  hover:scale-110 transform
                `}
              >
                {/* Glow effect */}
                <motion.div
                  className="absolute inset-0 rounded-full blur-lg opacity-0 group-hover:opacity-50"
                  style={{
                    background: `linear-gradient(45deg, ${action.color.split(' ')[2]}, ${action.color.split(' ')[4]})`
                  }}
                  transition={{ duration: 0.3 }}
                />
                
                <action.icon className="w-6 h-6 relative z-10" />
                
                {/* Tooltip */}
                <motion.div
                  initial={{ opacity: 0, x: 10 }}
                  whileHover={{ opacity: 1, x: 0 }}
                  className="absolute right-full mr-3 px-3 py-1 bg-black/80 text-white text-sm rounded-lg whitespace-nowrap"
                >
                  {action.label}
                </motion.div>
              </motion.button>
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Main button */}
      <motion.button
        onClick={handleMainButtonClick}
        className="relative w-16 h-16 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-full text-white shadow-2xl hover:shadow-3xl transition-all duration-300 flex items-center justify-center group"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        {/* Background animation */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent rounded-full"
          animate={{
            rotate: isOpen ? 180 : 0
          }}
          transition={{ duration: 0.3 }}
        />
        
        {/* Glow effect */}
        <motion.div
          className="absolute inset-0 rounded-full blur-xl opacity-0 group-hover:opacity-50"
          style={{
            background: 'linear-gradient(45deg, #3b82f6, #8b5cf6, #ec4899)'
          }}
          transition={{ duration: 0.3 }}
        />
        
        {/* Icon */}
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X className="w-8 h-8 relative z-10" />
            </motion.div>
          ) : (
            <motion.div
              key="plus"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Plus className="w-8 h-8 relative z-10" />
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Pulse effect */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0, 0.5]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute inset-0 rounded-full border-2 border-white/30"
        />
      </motion.button>
    </div>
  );
};

export default PremiumFloatingButton;
